import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  TextField,
  Grid,
  Avatar,
  Stack,
  Autocomplete,
} from '@mui/material'
import { PhotoCamera } from '@mui/icons-material'
import { fetchUpdateEmployerUser } from 'src/redux/employerUser'
import { States } from 'data/constants'
import { getPhysicianProfileImage } from 'src/pages/Physicians/helpers'
import { TextEditor } from 'components/TextEditor/TextEditor'

const AboutYou = ({ employerUser }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    id: employerUser.id,
    employerId: employerUser.employerId,
    firstName: employerUser.firstName || '',
    lastName: employerUser.lastName || '',
    jobTitle: employerUser.jobTitle || '',
    city: employerUser.city || '',
    state: employerUser.state || '',
    phone: employerUser.phone || '',
    email: employerUser.email || '',
    about: employerUser.about || '',
    aboutRaw: employerUser.aboutRaw || '',
  })
  const [isEditing, setIsEditing] = useState(false)
  const [profileImage, setProfileImage] = useState(null)

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleAboutChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      about: content.about,
      aboutRaw: content.aboutRaw,
    }))
  }

  const handleImageUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      setProfileImage(file)
      // In a real implementation, you would upload this to the server
      // For now, we'll just show a preview
    }
  }

  const handleSave = () => {
    const dataToSubmit = { ...formData }
    if (profileImage) {
      dataToSubmit.profileImageFile = profileImage
    }
    dispatch(fetchUpdateEmployerUser(dataToSubmit, 'Profile Updated!'))
    setIsEditing(false)
  }

  const handleCancel = () => {
    setFormData({
      id: employerUser.id,
      employerId: employerUser.employerId,
      firstName: employerUser.firstName || '',
      lastName: employerUser.lastName || '',
      jobTitle: employerUser.jobTitle || '',
      city: employerUser.city || '',
      state: employerUser.state || '',
      phone: employerUser.phone || '',
      email: employerUser.email || '',
      about: employerUser.about || '',
      aboutRaw: employerUser.aboutRaw || '',
    })
    setIsEditing(false)
    setProfileImage(null)
  }

  const profileImageUrl = getPhysicianProfileImage(
    profileImage ? URL.createObjectURL(profileImage) : employerUser.profileImage
  )

  return (
    <Box>
      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} justifyContent="flex-end">
        {isEditing ? (
          <>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Changes
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={() => setIsEditing(true)}>
            Edit Profile
          </Button>
        )}
      </Stack>

      {/* Profile Image Section */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          src={profileImageUrl}
          sx={{
            width: 120,
            height: 120,
            mx: 'auto',
            mb: 2,
            border: '3px solid',
            borderColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        {isEditing && (
          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCamera />}
            size="small"
          >
            Upload Photo
            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </Button>
        )}
      </Box>

      {/* Form Fields */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            disabled={!isEditing}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            disabled={!isEditing}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Job Title"
            value={formData.jobTitle}
            onChange={(e) => handleChange('jobTitle', e.target.value)}
            disabled={!isEditing}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            disabled={!isEditing}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={Object.keys(States).map((k) => ({ value: k, label: States[k] }))}
            getOptionLabel={(option) => option.label || ''}
            value={
              formData.state
                ? { value: formData.state, label: States[formData.state] }
                : null
            }
            onChange={(e, newValue) => handleChange('state', newValue?.value || '')}
            disabled={!isEditing}
            renderInput={(params) => (
              <TextField {...params} label="State" required variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            disabled={!isEditing}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            disabled={!isEditing}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextEditor
            label="About"
            placeholder="Tell us about yourself, your role, and what you're looking for in candidates..."
            initialValue={formData.aboutRaw || formData.about}
            onChange={handleAboutChange}
            isReadOnly={!isEditing}
            rows={10}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default AboutYou
